import { useState, useEffect, Fragment } from 'react';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Modal,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

// Icons
import ConstructionIcon from '@mui/icons-material/Construction';
import HouseIcon from '@mui/icons-material/House';
import KeyOffIcon from '@mui/icons-material/KeyOff';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PercentIcon from '@mui/icons-material/Percent';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AddIcon from '@mui/icons-material/Add';


// Utils
import messages from '../../static/messages';

// Services
import proposalsServices from '../../services/proposalsServices';

// Components
import OrderEquipmentModal from './OrderEquipmentModal';
import PaymentModal from './PaymentModal';

// Styles
import { button } from '../../static/theme-styles';
import { modal_style } from '../../static/styles';

const NotesBar = ({ data, id, refresh, systems, orders, publicId, installationSchedule, createdBy, proposal }) => {
  const [open, setOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [mode, setMode] = useState();
  const [additionalNotes, setAdditionNotes] = useState({
    orderEquipmentCompleted: {
      completed: false,
    },
    title24: {
      comment: null,
      completed: false,
    },
    permit: {
      comment: null,
      completed: false,
    },
    rebate: {
      comment: null,
      completed: false,
    },
    payment: {
      comment: null,
      completed: false,
    },
    other: {
      comment: null,
      completed: false,
    },
    commentFromAlex: {
      comment: null,
      completed: false,
    },
  });
  const [equipmentModalOpen, setEquipmentModalOpen] = useState(false);

  // Handler functions
  const handleOpen = (mode) => {
    setMode(mode);
    setOpen(true);
  };

  const handleClose = () => {
    setMode();
    setOpen(false);
  };

  const handleEquipmentModalOpen = () => {
    setEquipmentModalOpen(true);
  };

  const handleEquipmentModalClose = () => {
    setEquipmentModalOpen(false);
  };

  const handleChange = (event) => {
    const key = event.target.name;
    const val = key === 'completed' ? event.target.checked : event.target.value;

    setAdditionNotes((prevState) => ({
      ...prevState,
      [mode]: {
        ...prevState[mode],
        [key]: val,
      },
    }));
  };

  // Async functions
  const saveComment = async () => {
    try {
      const response = await proposalsServices.update(id, {
        additionalNotes,
      });

      if (response.status === 200) {
        handleClose();
        toast.success(`The comment was successfully updated`);
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const saveEquipmentNotes = async (notes) => {
    try {
      const response = await proposalsServices.update(id, {
        additionalNotes: notes,
      });

      if (response.status === 200) {
        setAdditionNotes(notes);
        toast.success('Status updated');
        refresh();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    if (data) setAdditionNotes(data);
  }, [data]);

  return (
    <Fragment>
      <Box sx={{ mt: 2, p: 2 }}>
        <ButtonGroup
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'center',
            '> button:not(:last-of-type)': {
              mr: 3,
              [theme.breakpoints.down('sm')]: {
                mr: 0,
              },
            },
          })}
        >
          <Tooltip title="Order Equipment">
            <IconButton onClick={handleEquipmentModalOpen}>
              <ConstructionIcon
                sx={{
                  width: { md: 50, xs: 25 },
                  height: { md: 50, xs: 25 },
                  color: additionalNotes?.orderEquipmentCompleted?.completed
                    ? 'green'
                    : orders?.length
                    ? 'orange'
                    : 'rgba(0, 0, 0, 0.54)',
                }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Title 24">
            <IconButton onClick={() => handleOpen('title24')}>
              <HouseIcon
                sx={{
                  width: { md: 50, xs: 25 },
                  height: { md: 50, xs: 25 },
                  color: additionalNotes?.title24?.completed
                    ? 'green'
                    : additionalNotes?.title24?.comment
                    ? 'red'
                    : 'rgba(0, 0, 0, 0.54)',
                }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Permit">
            <IconButton onClick={() => handleOpen('permit')}>
              {additionalNotes?.permit?.completed ? (
                <VpnKeyIcon
                  sx={{
                    width: { md: 50, xs: 25 },
                    height: { md: 50, xs: 25 },
                    color: 'green',
                  }}
                />
              ) : (
                <KeyOffIcon
                  sx={{
                    width: { md: 50, xs: 25 },
                    height: { md: 50, xs: 25 },
                    color: additionalNotes?.permit?.comment ? 'red' : 'rgba(0, 0, 0, 0.54)',
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Rebate">
            <IconButton onClick={() => handleOpen('rebate')}>
              <PercentIcon
                sx={{
                  width: { md: 50, xs: 25 },
                  height: { md: 50, xs: 25 },
                  color: additionalNotes?.rebate?.completed
                    ? 'green'
                    : additionalNotes?.rebate?.comment
                    ? 'red'
                    : 'rgba(0, 0, 0, 0.54)',
                }}
              />
            </IconButton>
          </Tooltip>

          <Tooltip title="Payment">
            <IconButton onClick={() => setPaymentModalOpen(true)}>
              {additionalNotes?.payment?.completed ? (
                <MonetizationOnIcon
                  sx={{
                    width: { md: 50, xs: 25 },
                    height: { md: 50, xs: 25 },
                    color: 'green',
                  }}
                />
              ) : (
                <CurrencyExchangeIcon
                  sx={{
                    width: { md: 50, xs: 25 },
                    height: { md: 50, xs: 25 },
                    color: additionalNotes?.payment?.comment || (proposal?.payments?.length > 0) ? 'red' : 'rgba(0, 0, 0, 0.54)',
                  }}
                />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Other">
            <IconButton onClick={() => handleOpen('other')}>
              <AddIcon
                sx={{
                  width: { md: 50, xs: 25 },
                  height: { md: 50, xs: 25 },
                  color: additionalNotes?.other?.completed
                    ? 'green'
                    : additionalNotes?.other?.comment
                    ? 'red'
                    : 'rgba(0, 0, 0, 0.54)',
                }}
              />
            </IconButton>
          </Tooltip>

        </ButtonGroup>
      </Box>

      {/* Standard Notes Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={modal_style}>
          <Typography variant="h5">
            <strong>
              {mode === 'commentFromAlex'
                ? 'Comment'
                : mode?.charAt(0).toUpperCase() +
                  mode
                    ?.slice(1)
                    .split(/(?=[A-Z])/)
                    .join(' ')}
            </strong>
          </Typography>

          <FormControlLabel
            onChange={handleChange}
            control={<Checkbox name="completed" checked={additionalNotes[mode]?.completed || false} />}
            label="Completed"
            sx={{ mt: 2 }}
          />

          <TextField
            label="Add a comment"
            multiline
            name="comment"
            rows={4}
            value={additionalNotes[mode]?.comment || ''}
            variant="filled"
            fullWidth
            onChange={handleChange}
            sx={{ mt: 2 }}
          />

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" sx={{ ...button('primary', 'secondary'), mr: 2 }} onClick={saveComment}>
              Save
            </Button>
            <Button variant="contained" onClick={handleClose} sx={button('secondary')}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Order Equipment Modal */}
      <OrderEquipmentModal
        open={equipmentModalOpen}
        onClose={handleEquipmentModalClose}
        proposal={proposal}
        systems={systems}
        orders={orders || []}
        publicId={publicId}
        proposalId={id}
        refresh={refresh}
        additionalNotes={additionalNotes}
        onSaveNotes={saveEquipmentNotes}
        installationSchedule={installationSchedule}
        createdBy={createdBy}
      />

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        proposalId={id}
        proposal={proposal}
        refresh={refresh}
      />
    </Fragment>
  );
};

export default NotesBar;
